import React, { useState, useEffect, useRef } from 'react';
import { Search, ShoppingCart, X, Plus, Minus, CheckCircle2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Camera } from 'lucide-react';
import { Toast } from '../common/Toast';
import { BarcodeScanner } from '../common/BarcodeScanner';
import { SaleSuccessReceipt, ReceiptItem } from './SaleSuccessReceipt';
import { useProducts, useFilterProducts, useProductSearch } from '../../hooks/useProducts';
import { productService } from '../../services/productService';
import { useCreateSale } from '../../hooks/useSales';
import { Product } from '../../types/Product';
import { SaleFormData } from '../../types/Sales';
import { formatCurrency } from '../../utils/formatters';
import { getErrorMessage } from '../../utils/errorMessages';

interface CartItem {
  product: Product;
  quantity: number;
  unitPrice: number;
  total: number;
}

export const SalesTab: React.FC = () => {
  const [searchByName, setSearchByName] = useState('');
  const [searchByBarcode, setSearchByBarcode] = useState('');
  const [searchByColor, setSearchByColor] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState<{ items: ReceiptItem[]; total: number } | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 50;

  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const createSale = useCreateSale();

  // İsim veya barkod: backend search API (name + code üzerinde arar).
  // Sadece mevcut sayfada client-side filtrelemek barkodu kaçırıyordu.
  const searchTerm = searchByName.trim() || searchByBarcode.trim();
  const { data: searchResults, isLoading: searchLoading } = useProductSearch(searchTerm);
  // Renk ile arama: backend filtre
  const filterParams = !searchTerm && searchByColor.trim()
    ? { page: currentPage, size: pageSize, sortBy: 'id' as const, sortDir: 'asc' as const, color: searchByColor.trim() }
    : null;
  const { data: filterPage, isLoading: filterLoading } = useFilterProducts(filterParams);
  const { data: productsPage, isLoading: productsLoading } = useProducts({ page: currentPage, size: pageSize });

  const products = searchTerm
    ? (searchResults ?? [])
    : searchByColor.trim()
      ? (filterPage?.content ?? [])
      : (productsPage?.content ?? []);
  const totalPages = searchTerm
    ? 1
    : searchByColor.trim()
      ? (filterPage?.totalPages ?? 0)
      : (productsPage?.totalPages ?? 0);
  const totalElements = searchTerm
    ? (searchResults?.length ?? 0)
    : searchByColor.trim()
      ? (filterPage?.totalElements ?? 0)
      : (productsPage?.totalElements ?? 0);
  const isLoadingProducts = searchTerm ? searchLoading : searchByColor.trim() ? filterLoading : productsLoading;

  // Auto-focus barcode input on mount and after operations
  useEffect(() => {
    barcodeInputRef.current?.focus();
  }, [cart]);

  // Sepete ürün eklendiğinde fişi kapat (yeni işlem başlıyor)
  useEffect(() => {
    if (showReceipt && cart.length > 0) {
      setShowReceipt(false);
      setReceiptData(null);
    }
  }, [cart.length, showReceipt]);

  // Filter products based on search criteria
  const filteredProducts = products.filter((product) => {
    const matchesName = !searchByName ||
      product.name.toLocaleLowerCase('tr-TR').startsWith(searchByName.toLocaleLowerCase('tr-TR'));
    const matchesBarcode = !searchByBarcode ||
      product.code?.toLocaleLowerCase('tr-TR').startsWith(searchByBarcode.toLocaleLowerCase('tr-TR'));
    const matchesColor = !searchByColor ||
      product.color?.toLocaleLowerCase('tr-TR').startsWith(searchByColor.toLocaleLowerCase('tr-TR'));

    return matchesName && matchesBarcode && matchesColor;
  });

  // Check if any search filter is active
  const hasActiveSearch = searchByName || searchByBarcode || searchByColor;

  // Handle search field changes - reset to first page
  const handleSearchChange = (field: 'name' | 'barcode' | 'color', value: string) => {
    if (field === 'name') setSearchByName(value);
    if (field === 'barcode') setSearchByBarcode(value);
    if (field === 'color') setSearchByColor(value);
    // Reset to first page when search changes
    if (currentPage !== 0) {
      setCurrentPage(0);
    }
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    // Scroll to top of product list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle barcode scan - shared logic for both Enter key and camera scan
  const handleBarcodeScanned = async (barcode: string) => {
    const trimmed = barcode.trim();
    if (!trimmed) return;

    // 1) Önce mevcut sayfadaki ürünlerde barkodu ara (hızlı yol)
    let product = products.find(
      (p) => p.code?.toLowerCase() === trimmed.toLowerCase()
    );

    // 2) Bulunamazsa backend search API ile tüm ürünlerde tara (isim + barkod)
    if (!product) {
      try {
        const results = await productService.search(trimmed);
        product = results.find(
          (p) => p.code?.toLowerCase() === trimmed.toLowerCase()
        );
      } catch (error) {
        // Arama hatasında kullanıcıya bilgi ver
        setToast({
          open: true,
          message: 'Barkod aranırken hata oluştu',
          severity: 'error',
        });
      }
    }

    if (product) {
      addToCart(product);
      setSearchByBarcode('');
      // Reset to first page if not already there
      if (currentPage !== 0) {
        setCurrentPage(0);
      }
    } else {
      setToast({
        open: true,
        message: 'Bu barkoda ait ürün bulunamadı',
        severity: 'error',
      });
    }
  };

  // Handle barcode scan (Enter key)
  const handleBarcodeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchByBarcode.trim()) {
      void handleBarcodeScanned(searchByBarcode);
    }
  };

  // Add product to cart
  const addToCart = (product: Product, quantity: number = 1) => {
    if (product.quantity < quantity) {
      setToast({
        open: true,
        message: `Yetersiz stok! Mevcut: ${product.quantity}`,
        severity: 'error',
      });
      return;
    }

    const existingItem = cart.find((item) => item.product.id === product.id);
    const total = product.retailPrice * quantity;
    
    if (existingItem) {
      const newQuantity = existingItem.quantity + quantity;
      if (product.quantity < newQuantity) {
        setToast({
          open: true,
          message: `Yetersiz stok! Mevcut: ${product.quantity}`,
          severity: 'error',
        });
        return;
      }
      const newTotal = product.retailPrice * newQuantity;
      
      setCart(
        cart.map((item) =>
          item.product.id === product.id
            ? {
                ...item,
                quantity: newQuantity,
                unitPrice: product.retailPrice,
                total: newTotal,
              }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          product,
          quantity,
          unitPrice: product.retailPrice,
          total,
        },
      ]);
    }
    
    // Clear search fields
    setSearchByName('');
    setSearchByColor('');
    barcodeInputRef.current?.focus();
  };

  // Update cart item quantity
  const updateCartQuantity = (productId: number, delta: number) => {
    setCart((prevCart) => {
      return prevCart
        .map((item) => {
          if (item.product.id === productId) {
            const newQuantity = item.quantity + delta;
            if (newQuantity <= 0) return null;
            if (item.product.quantity < newQuantity) {
              setToast({
                open: true,
                message: `Yetersiz stok! Mevcut: ${item.product.quantity}`,
                severity: 'error',
              });
              return item;
            }
            const newTotal = item.unitPrice * newQuantity;
            return {
              ...item,
              quantity: newQuantity,
              total: newTotal,
            };
          }
          return item;
        })
        .filter((item): item is CartItem => item !== null);
    });
  };

  // Remove item from cart
  const removeFromCart = (productId: number) => {
    setCart(cart.filter((item) => item.product.id !== productId));
  };

  // Calculate total
  const cartTotal = cart.reduce((sum, item) => sum + item.total, 0);

  // Complete sale
  const handleCompleteSale = async () => {
    if (cart.length === 0) {
      setToast({
        open: true,
        message: 'Sepet boş',
        severity: 'error',
      });
      return;
    }

    try {
      // Create sales for each cart item
      const salesPromises = cart.map((item) => {
        const saleData: SaleFormData = {
          productId: item.product.id,
          quantitySold: item.quantity,
          totalEarning: item.total,
          saleDate: new Date().toISOString(),
        };
        return createSale.mutateAsync(saleData);
      });

      await Promise.all(salesPromises);

      // Fiş göster (POS receipt animasyonu)
      setReceiptData({
        items: cart.map((item) => ({
          name: item.product.color
            ? `${item.product.name} · ${item.product.color}`
            : item.product.name,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          total: item.total,
        })),
        total: cartTotal,
      });
      setShowReceipt(true);

      // Reset
      setCart([]);
      setSearchByName('');
      setSearchByBarcode('');
      setSearchByColor('');
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Satış sırasında hata oluştu');
      setToast({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Search and Product Selection */}
        <div className="lg:col-span-2 space-y-4">
          {/* Search Section */}
          <div className="card p-4 space-y-3">
            <h3 className="text-sm font-semibold text-foreground mb-3">Ürün Arama</h3>
            
            <div>
              <label className="block text-xs font-medium text-foreground-secondary mb-1.5">
                İsim ile Ara
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-tertiary" />
                <input
                  type="text"
                  value={searchByName}
                  onChange={(e) => handleSearchChange('name', e.target.value)}
                  placeholder="Ürün adı..."
                  className="input pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-secondary mb-1.5">
                Barkod ile Ara <span className="text-accent">(Enter)</span>
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-tertiary" />
                <input
                  ref={barcodeInputRef}
                  type="text"
                  value={searchByBarcode}
                  onChange={(e) => setSearchByBarcode(e.target.value)}
                  onKeyPress={handleBarcodeKeyPress}
                  placeholder="Barkod taratın veya girin..."
                  className="input pl-10 pr-10 border-accent/50 focus:border-accent"
                />
                <button
                  onClick={() => setScannerOpen(true)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center hover:opacity-70 transition-opacity"
                  type="button"
                  title="Barkod Tara"
                >
                  <Camera className="w-4 h-4 text-accent" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-secondary mb-1.5">
                Renk ile Ara
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-tertiary" />
                <input
                  type="text"
                  value={searchByColor}
                  onChange={(e) => handleSearchChange('color', e.target.value)}
                  placeholder="Renk..."
                  className="input pl-10"
                />
              </div>
            </div>
          </div>

          {/* Product List */}
          <div className="card">
            <div className="p-4 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">
                Ürünler {hasActiveSearch ? `(${filteredProducts.length})` : `(${totalElements})`}
              </h3>
            </div>
            <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
              {isLoadingProducts ? (
                <div className="p-8 text-center text-foreground-secondary">
                  Yükleniyor...
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="p-8 text-center text-foreground-secondary">
                  Ürün bulunamadı
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredProducts.map((product) => {
                    const outOfStock = product.quantity <= 0;
                    const inCart = cart.find(item => item.product.id === product.id);
                    const remainingStock = product.quantity - (inCart?.quantity ?? 0);
                    const cannotAdd = outOfStock || remainingStock <= 0;
                    return (
                      <div
                        key={product.id}
                        className={`p-4 transition-smooth ${cannotAdd ? 'opacity-50 cursor-not-allowed' : 'hover:bg-background-tertiary cursor-pointer'}`}
                        onClick={() => !cannotAdd && addToCart(product)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`font-medium ${cannotAdd ? 'text-foreground-secondary' : 'text-foreground'}`}>{product.name}</span>
                              {product.brand && (
                                <span className="text-xs text-foreground-tertiary">
                                  {product.brand}
                                </span>
                              )}
                              {outOfStock && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-error/10 text-error">
                                  Stokta Yok
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-foreground-secondary">
                              {product.color && <span>Renk: {product.color}</span>}
                              {product.code && <span>Barkod: {product.code}</span>}
                              <span className={outOfStock ? 'text-error' : ''}>Stok: {product.quantity}</span>
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className={`font-semibold ${cannotAdd ? 'text-foreground-secondary' : 'text-foreground'}`}>
                              {formatCurrency(product.retailPrice)}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!cannotAdd) addToCart(product);
                              }}
                              disabled={cannotAdd}
                              className="mt-1 px-3 py-1 text-xs btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Ekle
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Pagination - Only show when no active search filters */}
            {!hasActiveSearch && productsPage && totalPages > 1 && (
              <div className="p-4 border-t border-border overflow-x-hidden max-w-full">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-0">
                  <div className="text-xs md:text-sm text-foreground-secondary whitespace-nowrap">
                    Toplam {totalElements} ürün - Sayfa {currentPage + 1} / {totalPages}
                  </div>
                  <div className="flex items-center gap-1 md:gap-2 w-full md:w-auto overflow-x-auto md:overflow-x-visible max-w-full">
                    {/* En Başa Git */}
                    <button
                      onClick={() => handlePageChange(0)}
                      disabled={currentPage === 0 || isLoadingProducts}
                      className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg bg-background-tertiary text-foreground border border-border hover:bg-background-secondary transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                      title="En başa git"
                    >
                      <ChevronsLeft className="w-3.5 h-3.5 md:w-4 md:h-4" />
                    </button>
                    
                    {/* Önceki Sayfa */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0 || isLoadingProducts}
                      className="flex items-center justify-center gap-1 md:gap-2 px-2 md:px-4 h-10 rounded-lg bg-background-tertiary text-foreground border border-border hover:bg-background-secondary transition-smooth disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm font-medium whitespace-nowrap"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline">Önceki</span>
                    </button>
                    
                    {/* Sayfa Numaraları */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i;
                        } else if (currentPage < 2) {
                          pageNum = i;
                        } else if (currentPage > totalPages - 3) {
                          pageNum = totalPages - 5 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={isLoadingProducts}
                            className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg text-xs md:text-sm font-medium transition-smooth ${
                              currentPage === pageNum
                                ? 'bg-accent text-white'
                                : 'bg-background-tertiary text-foreground hover:bg-background-secondary border border-border'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {pageNum + 1}
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Sonraki Sayfa */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages - 1 || isLoadingProducts}
                      className="flex items-center justify-center gap-1 md:gap-2 px-2 md:px-4 h-10 rounded-lg bg-background-tertiary text-foreground border border-border hover:bg-background-secondary transition-smooth disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm font-medium whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">Sonraki</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    
                    {/* Sona Git */}
                    <button
                      onClick={() => handlePageChange(totalPages - 1)}
                      disabled={currentPage >= totalPages - 1 || isLoadingProducts}
                      className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg bg-background-tertiary text-foreground border border-border hover:bg-background-secondary transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                      title="Sona git"
                    >
                      <ChevronsRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Cart */}
        <div className="space-y-4">
          {/* Cart */}
          <div className="card">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Sepet</h3>
                <span className="text-xs text-foreground-secondary">
                  {cart.length} ürün
                </span>
              </div>
            </div>
            
            <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
              {cart.length === 0 ? (
                <div className="p-8 text-center text-foreground-secondary">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Sepet boş</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {cart.map((item) => (
                    <div key={item.product.id} className="p-3">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-foreground truncate">
                            {item.product.name}
                            {item.product.color ? ` · ${item.product.color}` : ''}
                          </div>
                          <div className="text-xs text-foreground-secondary">
                            {formatCurrency(item.unitPrice)} × {item.quantity}
                          </div>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.product.id)}
                          className="p-1 hover:bg-background-tertiary rounded transition-smooth ml-2"
                        >
                          <X className="w-4 h-4 text-foreground-secondary" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateCartQuantity(item.product.id, -1)}
                            className="p-1 rounded bg-background-tertiary hover:bg-background-secondary transition-smooth"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-sm font-medium text-foreground w-8 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateCartQuantity(item.product.id, 1)}
                            className="p-1 rounded bg-background-tertiary hover:bg-background-secondary transition-smooth"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="font-semibold text-foreground">
                          {formatCurrency(item.total)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Total and Complete Button */}
            {cart.length > 0 && (
              <div className="p-4 border-t border-border bg-background-tertiary">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-foreground-secondary">
                    Kasa Tutarı
                  </span>
                  <span className="text-xl font-bold text-foreground">
                    {formatCurrency(cartTotal)}
                  </span>
                </div>
                <button
                  onClick={handleCompleteSale}
                  disabled={createSale.isPending}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {createSale.isPending ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      İşleniyor...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Satışı Tamamla
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Sepetin altından çıkan fiş */}
          <SaleSuccessReceipt
            open={showReceipt}
            onClose={() => {
              setShowReceipt(false);
              setReceiptData(null);
              barcodeInputRef.current?.focus();
            }}
            items={receiptData?.items ?? []}
            total={receiptData?.total ?? 0}
          />
        </div>
      </div>

      <Toast
        open={toast.open}
        onClose={() => setToast({ ...toast, open: false })}
        message={toast.message}
        severity={toast.severity}
      />

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleBarcodeScanned}
      />
    </div>
  );
};
